import React from "react";

/**
 * Global React error boundary. Catches uncaught JS errors from descendant
 * components and renders a friendly fallback instead of white screen.
 * Errors are sent to backend log endpoint for triage.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log to console (kept for dev) AND ship to backend if possible
    console.error("[ErrorBoundary]", error, info);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("tt_token");
      fetch(`${backendUrl}/api/feedback/client-error`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: String(error?.message || error || "Unknown"),
          stack: String(error?.stack || "").slice(0, 4000),
          component_stack: String(info?.componentStack || "").slice(0, 2000),
          path: window.location.pathname,
          user_agent: navigator.userAgent.slice(0, 300),
        }),
      }).then((r) => r.json()).then((d) => {
        if (d?.id) this.setState({ errorId: d.id });
      }).catch(() => {});
    } catch (_) {/* swallow */}
  }

  handleReload = () => { window.location.reload(); };
  handleHome = () => { window.location.href = "/dashboard"; };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--tt-bg,#fdf8f1)] p-6"
           data-testid="error-boundary">
        <div className="max-w-md w-full bg-[var(--tt-surface)] rounded-lg border border-[var(--tt-border,#e5dccb)] shadow-sm p-6 text-center">
          <div className="text-5xl mb-3">⚠️</div>
          <h1 className="text-xl font-semibold tt-display mb-2">Coś poszło nie tak</h1>
          <p className="text-sm text-[var(--tt-muted,#6b6b6b)] mb-4">
            Aplikacja napotkała nieoczekiwany błąd. Spróbuj odświeżyć stronę.
            Jeśli problem się powtórzy, zgłoś go przez przycisk „Zgłoś problem&rdquo;.
          </p>
          {this.state.errorId && (
            <p className="text-xs tt-mono mb-3 text-[var(--tt-muted,#6b6b6b)]">
              ID błędu: {this.state.errorId}
            </p>
          )}
          <div className="flex gap-2 justify-center">
            <button onClick={this.handleReload}
                    className="px-4 py-2 rounded bg-[var(--tt-primary,#6b1c2c)] text-[var(--tt-primary-fg,#ffffff)] text-sm hover:opacity-90"
                    data-testid="error-reload-btn">
              Odśwież stronę
            </button>
            <button onClick={this.handleHome}
                    className="px-4 py-2 rounded border border-[var(--tt-border,#e5dccb)] text-sm hover:bg-slate-50"
                    data-testid="error-home-btn">
              Wróć do dashboardu
            </button>
          </div>
        </div>
      </div>
    );
  }
}
